import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';

interface PrivateDigestAccessCardProps {
    isAuthenticated: boolean;
    onSignIn: () => void;
    onSwitchAccount: () => void;
    fontsLoaded?: boolean;
}

export default function PrivateDigestAccessCard({
    isAuthenticated,
    onSignIn,
    onSwitchAccount,
    fontsLoaded = false,
}: PrivateDigestAccessCardProps) {
    return (
        <View style={styles.privateCard}>
            <View style={styles.iconContainer}>
                <FontAwesome6 name="lock" size={28} color="#d97706" />
            </View>

            <Text style={[styles.title, fontsLoaded && interSemiBold]}><Trans id="digest.privatedigestaccesscard.privateDigest" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                Private Digest
            </Trans></Text>

            {isAuthenticated ? (
                <>
                    <Text style={styles.bodyText}><Trans id="digest.privatedigestaccesscard.youDonTHaveAccessToThisDigestItMayBelong" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                        You don't have access to this digest. It may belong to a different account or hasn't been shared with you.
                    </Trans></Text>

                    <View style={styles.actionSection}>
                        <Text style={[styles.actionTitle, fontsLoaded && interSemiBold]}><Trans id="digest.privatedigestaccesscard.whatYouCanDo" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                            What you can do:
                        </Trans></Text>

                        <View style={styles.actionItem}>
                            <View style={styles.actionIconCircle}>
                                <FontAwesome6 name="share-from-square" size={14} color={colors.primaryGreen} />
                            </View>
                            <Text style={styles.actionText}><Trans id="digest.privatedigestaccesscard.askThePersonWhoSentYouThisLinkToShareaa47bb" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                                Ask the person who sent you this link to share the digest with you using the Share button
                            </Trans></Text>
                        </View>

                        <View style={styles.actionItem}>
                            <View style={styles.actionIconCircle}>
                                <FontAwesome6 name="globe" size={14} color={colors.primaryGreen} />
                            </View>
                            <Text style={styles.actionText}><Trans id="digest.privatedigestaccesscard.orAskThemToMakeTheDigestPublicSoAnyone" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                                Or ask them to make the digest public so anyone can view it
                            </Trans></Text>
                        </View>

                        <View style={styles.actionItem}>
                            <View style={styles.actionIconCircle}>
                                <FontAwesome6 name="right-left" size={14} color={colors.primaryGreen} />
                            </View>
                            <Text style={styles.actionText}><Trans id="digest.privatedigestaccesscard.ifThisIsYourDigestTrySigningInWithA" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                                If this is your digest, try signing in with a different account
                            </Trans></Text>
                        </View>
                    </View>

                    <TouchableOpacity
                        style={styles.secondaryButton}
                        onPress={onSwitchAccount}
                        activeOpacity={0.8}
                    >
                        <FontAwesome6 name="right-left" size={14} color={colors.primaryGreen} />
                        <Text style={[styles.secondaryButtonText, fontsLoaded && interSemiBold]}><Trans id="digest.privatedigestaccesscard.switchAccount" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                            Switch Account
                        </Trans></Text>
                    </TouchableOpacity>
                </>
            ) : (
                <>
                    <Text style={styles.bodyText}><Trans id="digest.privatedigestaccesscard.thisDigestIsPrivateIfThisIsYourDigestSign" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                        This digest is private. If this is your digest, sign in to view it.
                    </Trans></Text>

                    <TouchableOpacity
                        style={styles.signInButton}
                        onPress={onSignIn}
                        activeOpacity={0.8}
                    >
                        <FontAwesome6 name="right-to-bracket" size={16} color="#ffffff" />
                        <Text style={[styles.signInButtonText, fontsLoaded && interSemiBold]}><Trans id="digest.privatedigestaccesscard.signIn" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                            Sign In
                        </Trans></Text>
                    </TouchableOpacity>

                    <View style={styles.divider}>
                        <View style={styles.dividerLine} />
                        <Text style={styles.dividerText}><Trans id="digest.privatedigestaccesscard.or" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">or</Trans></Text>
                        <View style={styles.dividerLine} />
                    </View>

                    <View style={styles.actionSectionNoBorder}>
                        <Text style={[styles.actionTitle, fontsLoaded && interSemiBold]}><Trans id="digest.privatedigestaccesscard.notYourDigest" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                            Not your digest?
                        </Trans></Text>

                        <View style={styles.actionItem}>
                            <View style={styles.actionIconCircle}>
                                <FontAwesome6 name="share-from-square" size={14} color={colors.primaryGreen} />
                            </View>
                            <Text style={styles.actionText}><Trans id="digest.privatedigestaccesscard.askThePersonWhoSentYouThisLinkToShare" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                                Ask the person who sent you this link to share it with you using the Share button
                            </Trans></Text>
                        </View>

                        <View style={styles.actionItem}>
                            <View style={styles.actionIconCircle}>
                                <FontAwesome6 name="globe" size={14} color={colors.primaryGreen} />
                            </View>
                            <Text style={styles.actionText}><Trans id="digest.privatedigestaccesscard.orAskThemToMakeTheDigestPublic" comment="UI copy in components/digest/PrivateDigestAccessCard.tsx">
                                Or ask them to make the digest public
                            </Trans></Text>
                        </View>
                    </View>
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    privateCard: {
        width: '100%',
        maxWidth: 620,
        backgroundColor: colors.card,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.border,
        padding: 32,
        marginBottom: 24,
        alignItems: 'center',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        }),
    },
    iconContainer: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#fff7ed',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    title: {
        fontSize: 32,
        color: colors.text900,
        marginBottom: 16,
        textAlign: 'center',
    },
    bodyText: {
        fontSize: 17,
        color: colors.text700,
        lineHeight: 28,
        textAlign: 'center',
        marginBottom: 24,
        maxWidth: 480,
    },
    actionSection: {
        width: '100%',
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: 20,
        marginBottom: 24,
    },
    actionSectionNoBorder: {
        width: '100%',
        paddingTop: 8,
    },
    actionTitle: {
        fontSize: 16,
        color: colors.text900,
        marginBottom: 14,
    },
    actionItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 12,
    },
    actionIconCircle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: colors.primary100,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
        marginTop: 2,
    },
    actionText: {
        flex: 1,
        fontSize: 14,
        color: colors.text700,
        lineHeight: 22,
    },
    secondaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bgWeak,
        borderWidth: 1,
        borderColor: colors.primaryGreen,
        borderRadius: 8,
        paddingVertical: 12,
        paddingHorizontal: 24,
        gap: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    secondaryButtonText: {
        fontSize: 16,
        color: colors.primaryGreen,
    },
    signInButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingHorizontal: 24,
        paddingVertical: 12,
        gap: 8,
        marginBottom: 24,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    signInButtonText: {
        fontSize: 16,
        color: '#ffffff',
    },
    divider: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        marginBottom: 24,
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: colors.border,
    },
    dividerText: {
        marginHorizontal: 12,
        fontSize: 13,
        color: colors.text500,
        textTransform: 'uppercase',
    },
});
